import React from 'react';
import { Card } from './Card';
import { HistoryBadge } from './HistoryBadge';
import { formatCompactCurrency, formatVariance } from '../utils/formatters';
import { getMonthStatus } from '../utils/fiscalYear';

interface MonthData {
  month: number;
  label: string;
  budget: number;
  forecast: number;
  actual: number;
}

interface MonthlyBarChartProps {
  data: MonthData[];
  /** Optional: previous-meeting actual per month for inline ± deltas. */
  previousActualByMonth?: Record<number, number | null>;
}

/**
 * Renders 12 monthly cards in a responsive grid. Each card shows one month
 * with budget + actual/forecast bars and variance — same visual language as
 * the existing QuarterlyChart but finer-grained.
 */
export const MonthlyBarChart: React.FC<MonthlyBarChartProps> = ({ data, previousActualByMonth }) => {
  if (!data || data.length === 0) {
    return (
      <Card>
        <p className="text-sm text-text-muted text-center py-4">No monthly data available</p>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
      {data.map((m) => {
        const status = getMonthStatus(m.month);
        const secondBarValue =
          status === 'past' ? m.actual :
          status === 'future' ? m.forecast :
          m.actual + m.forecast;
        const maxValue = Math.max(m.budget, secondBarValue, 1);
        const budgetWidth = (m.budget / maxValue) * 100;
        const actualWidth = (m.actual / maxValue) * 100;
        const forecastWidth = (m.forecast / maxValue) * 100;

        const isOnTrack = secondBarValue >= m.budget * 0.95;
        const variance = secondBarValue - m.budget;

        const secondLabel =
          status === 'past' ? 'Actual' :
          status === 'future' ? 'Forecast' :
          'Act+Fcast';

        return (
          <Card key={m.month}>
            <div className="flex justify-between items-start mb-3">
              <h4 className="font-semibold text-text-primary text-sm">{m.label}</h4>
              <div className="flex flex-col items-end gap-1">
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    isOnTrack ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'
                  }`}
                >
                  {isOnTrack ? 'On Track' : 'Below'}
                </span>
                {previousActualByMonth && previousActualByMonth[m.month] !== undefined && (
                  <HistoryBadge
                    current={m.actual}
                    previous={previousActualByMonth[m.month]}
                    label="vs last meeting"
                    showNeutral={false}
                  />
                )}
              </div>
            </div>
            <div className="space-y-2">
              {/* Budget bar */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-blue-400 w-14">Budget</span>
                <div className="flex-1 bg-gray-700 rounded h-3 overflow-hidden">
                  <div className="h-3 bg-blue-500 rounded" style={{ width: `${budgetWidth}%` }} />
                </div>
                <span className="text-xs text-text-primary w-14 text-right">{formatCompactCurrency(m.budget)}</span>
              </div>

              {/* Actual / Forecast bar */}
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs w-14 ${
                    status === 'past' ? 'text-green-400' : status === 'future' ? 'text-orange-400' : 'text-white'
                  }`}
                >
                  {secondLabel}
                </span>
                <div className="flex-1 bg-gray-700 rounded h-3 overflow-hidden flex">
                  {status === 'past' && (
                    <div className="h-3 bg-green-500 rounded" style={{ width: `${actualWidth}%` }} />
                  )}
                  {status === 'current' && (
                    <>
                      <div className="h-3 bg-green-500" style={{ width: `${actualWidth}%` }} />
                      <div className="h-3 bg-orange-500" style={{ width: `${forecastWidth}%` }} />
                    </>
                  )}
                  {status === 'future' && (
                    <div className="h-3 bg-orange-500 rounded" style={{ width: `${forecastWidth}%` }} />
                  )}
                </div>
                <span className="text-xs text-text-primary w-14 text-right">{formatCompactCurrency(secondBarValue)}</span>
              </div>

              {/* Variance vs budget — (actual+forecast) − budget */}
              <div className="text-center">
                <span className={`text-xs font-semibold ${variance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatVariance(variance)}
                </span>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
